import { useCallback, useEffect, useMemo, useRef } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { track } from 'app/analytics';
import { store } from 'app/store';
import {
  projectBoardApi,
  useGetProjectQuery,
  useGetAhuUnitsQuery,
  useGetAhuConfigurationsQuery,
  useGetAhuWeatherRatingsQuery,
  useGetAhuCmsProductsQuery,
  useGetAllPermissionsQuery,
  useCreateAhuMutation,
  useRenameAhuMutation,
  useDeleteAhuMutation,
  useDuplicateAhuMutation,
  useCreateAhuVersionMutation,
  useMakeMainVersionMutation,
  useReorderAhuMainsMutation,
  useReorderAhuVersionsMutation,
} from 'app/services/projectBoardApi';
import {
  selectAhu,
  toggleVersionParent,
  expandVersionParent,
  setDuplicatedModelId,
  addBusyAhu,
  removeBusyAhu,
  setAhuRightPreloader,
  setRenamingAhu,
  openNewAhu,
  closeNewAhu,
  openDuplicateAhu,
  closeDuplicateAhu,
  openCreateVersion,
  closeCreateVersion,
  openDeleteAhu,
  closeDeleteAhu,
} from 'app/slices/projectBoardSlice';
import { useActualTabs } from '../../hooks/useProjectBoardData';
import { useAhuSocket } from '../../hooks/useAhuSocket';
import { useAhuContextMenu } from '../../hooks/useAhuContextMenu';
import { useAhuDragOrder } from '../../hooks/useAhuDragOrder';
import {
  buildAhuTree,
  flattenAhuTree,
  ahuDisplayTag,
  type AhuMainGroup,
} from '../../ahuUtils';
import AhuRow, { type AhuRowAction } from './AhuRow';
import AhuInfoBar from './AhuInfoBar';
import NewAhuPopup from './NewAhuPopup';
import DuplicateAhuPopup from './DuplicateAhuPopup';
import CreateVersionPopup from './CreateVersionPopup';
import DeleteAhuPopup from './DeleteAhuPopup';

/**
 * Embedded AHU list (`ahus/view/ahus.js`, el `#main_ahus` — inventory Part B
 * §B1, parity 126-155). Renders the live part of `ahus/tmpl/ahus.tmpl`:
 * 28%/72% split (exact inline calc() widths), "Air Handling Unit" header +
 * ADD, scrollable `#ahu_list` mains+versions tree, `hr.left_properties`
 * divider, the `#ahus_info-bar` right pane, and the `#new_ahu_popup` mount.
 *
 * [NEG] honored: the legacy static-versions sub-list (B1.9 / parity 154) —
 * rows, handlers, SaveVersionPopup, `duplicate_static_version_status` socket
 * branch, keydown static branches and the per-click
 * `GET /ahu/duplicate/unit_static_version_view/` are NOT rebuilt; static
 * versions appear only as version rows with the "static" badge. Also
 * skipped: `with_editable_ahus`, the `ahu:click` kinetix-bot listener, the
 * `#kinetix_bot_button` toggle no-op, and the decorative `last-third-child`
 * pass (parity 155 / B1.3 quirk).
 */
const AhuListPanel = ({ projectId }: { projectId: number }) => {
  const dispatch = useAppDispatch();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const { data: project } = useGetProjectQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const actualTabs = useActualTabs(projectId);

  const { data: units } = useGetAhuUnitsQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const { data: configurations } = useGetAhuConfigurationsQuery();
  const { data: weatherRatings } = useGetAhuWeatherRatingsQuery();
  // CMS products with the PROJECT's cms_id — NOT hardcoded 1 (parity 128).
  const { data: cmsProducts } = useGetAhuCmsProductsQuery(
    project?.cms_id ?? skipToken
  );
  // allPermissions — RTK cache = the legacy stateStorage session cache
  // (fetched only on miss, parity 129).
  const { data: allPermissions } = useGetAllPermissionsQuery();

  const [createAhu] = useCreateAhuMutation();
  const [renameAhu] = useRenameAhuMutation();
  const [deleteAhu] = useDeleteAhuMutation();
  const [duplicateAhu] = useDuplicateAhuMutation();
  const [createAhuVersion] = useCreateAhuVersionMutation();
  const [makeMainVersion] = useMakeMainVersionMutation();
  const [reorderMains] = useReorderAhuMainsMutation();
  const [reorderVersions] = useReorderAhuVersionsMutation();

  const ahusState = useAppSelector((s) => s.projectBoard.ahus);
  const {
    selectedAhuId,
    expandedVersionParents,
    renamingAhuId,
    rightPreloader,
    modals,
  } = ahusState;

  const editable = !!project?.editable;

  // ---- Permission flags (B1.2 / parity 130, 135) ----
  const createAhuPermission = useMemo(
    () => !!actualTabs?.find((t) => t.name === 'Main')?.access,
    [actualTabs]
  );
  const editCreateAhuPermission = useMemo(
    () =>
      !!allPermissions?.[0]?.permissions?.ahu_board_items?.find(
        (el) => el.item === 'ahu'
      )?.access,
    [allPermissions]
  );
  const canMutate = createAhuPermission && editCreateAhuPermission;
  const addLocked = !editable || !canMutate;

  // Boot preloader until ALL parallel fetches settle (B1.2 / parity 127).
  const booted = !!(
    units &&
    configurations &&
    weatherRatings &&
    cmsProducts &&
    allPermissions &&
    actualTabs
  );

  // ---- Tree (B1.3) ----
  const groups = useMemo(() => buildAhuTree(units ?? []), [units]);
  const flatRows = useMemo(() => flattenAhuTree(groups), [groups]);
  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  const flatRowsRef = useRef(flatRows);
  flatRowsRef.current = flatRows;

  // ---- Row click (showAhuData): a click on a main row ALSO toggles its
  // versions container; re-click of the selected row short-circuits (B1.4).
  const rowClick = useCallback(
    (id: number) => {
      const group = groupsRef.current.find((g) => g.main.unit.id === id);
      if (group && group.versions.length) dispatch(toggleVersionParent(id));
      const current = store.getState().projectBoard.ahus.selectedAhuId;
      if (current === id) return;
      dispatch(selectAhu(id));
    },
    [dispatch]
  );

  // ---- KA-5238: duplicate re-entry guard + 60s watchdog ----
  // Mirrors the Backbone view's `_duplicating` flag, `_duplicateWatchdog`
  // timer and `_finishDuplicate()` teardown (ahus/view/ahus.js).
  const duplicatingRef = useRef(false);
  // Where the in-flight duplicate goes, for ahu_duplicated (analytics only).
  const duplicateTargetRef = useRef<'same_project' | 'other_project'>('same_project');
  const duplicateWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const clearDuplicateWatchdog = useCallback(() => {
    if (duplicateWatchdogRef.current) {
      clearTimeout(duplicateWatchdogRef.current);
      duplicateWatchdogRef.current = null;
    }
  }, []);
  // Tear down the in-flight duplication UI state: clear the watchdog, drop
  // the re-entry guard, and remove the spinner. Called when duplication
  // completes (socket) or when the watchdog fires. (KA-5238)
  const finishDuplicate = useCallback(() => {
    clearDuplicateWatchdog();
    duplicatingRef.current = false;
    dispatch(setAhuRightPreloader(false));
  }, [clearDuplicateWatchdog, dispatch]);
  useEffect(() => clearDuplicateWatchdog, [clearDuplicateWatchdog]);

  // Socket-driven completions (B1.8) — the ONLY refresh path for
  // add/duplicate/version (OQ B-1: no socket → legacy silent no-refresh).
  useAhuSocket(
    projectId,
    (id) => {
      dispatch(selectAhu(null));
      rowClick(id);
    },
    (success) => {
      // Backend reported a duplicate_status: stop the spinner and release
      // the guard; on non-success surface the failure instead of hanging.
      // (KA-5238)
      // Only this tab's own duplicate (the guard is still up) is counted.
      if (success && duplicatingRef.current)
        track('ahu_duplicated', { target: duplicateTargetRef.current });
      finishDuplicate();
      if (!success) window.alert('Duplication failed. Please try again.');
    }
  );

  // Right-click context menu over the rows (B1.4 / parity 140).
  useAhuContextMenu(panelRef, booted);

  // ---- Initial auto-selection (B1.3 / parity 137):
  // returnProjectBoard localStorage row (one-shot) else first row.
  const bootSelectedRef = useRef(false);
  useEffect(() => {
    if (bootSelectedRef.current || !booted) return;
    // The legacy auto-click happens exactly once at init even when the list
    // is empty (`.project_0` click is a no-op then).
    bootSelectedRef.current = true;
    if (!groupsRef.current.length) return;
    const returnId = window.localStorage.getItem('returnProjectBoard');
    if (returnId) {
      window.localStorage.removeItem('returnProjectBoard');
      const id = Number(returnId);
      if (flatRowsRef.current.some((r) => r.unit.id === id)) {
        rowClick(id);
        return;
      }
    }
    rowClick(groupsRef.current[0].main.unit.id);
  }, [booted, rowClick]);

  // Selection fallback: when the selected row disappears (own delete /
  // foreign delete refetch) → first row (deleteAhu + collection 'remove').
  useEffect(() => {
    if (!booted || !bootSelectedRef.current) return;
    if (selectedAhuId == null) return;
    if (!flatRows.some((r) => r.unit.id === selectedAhuId)) {
      dispatch(selectAhu(null));
      if (groups.length) rowClick(groups[0].main.unit.id);
    }
  }, [booted, flatRows, groups, selectedAhuId, dispatch, rowClick]);

  // ---- #ahu_list focus for arrow-key nav (render(), B1.1) ----
  useEffect(() => {
    if (booted) listRef.current?.focus();
  }, [booted]);

  // ---- Keyboard ↑/↓/Enter (body keydown; only while #project hash and no
  // popup — B1.4 / parity 138) ----
  useEffect(() => {
    if (!booted) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        document.querySelector('.popup-wrapper.active') ||
        window.location.hash !== '#project'
      )
        return;
      if (e.keyCode !== 38 && e.keyCode !== 40 && e.keyCode !== 13) return;
      const rows = flatRowsRef.current;
      const current = store.getState().projectBoard.ahus.selectedAhuId;
      const index = rows.findIndex((r) => r.unit.id === current);
      if (e.keyCode === 13) {
        if (current != null) openAhu(current);
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      const nextIndex = e.keyCode === 38 ? index - 1 : index + 1;
      const next = rows[nextIndex];
      if (!next) return;
      rowClick(next.unit.id);
      document
        .querySelector(`#ahu_list .left-bar-lists[data-index="${nextIndex}"]`)
        ?.scrollIntoView();
    };
    document.body.addEventListener('keydown', onKeyDown);
    return () => document.body.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booted, rowClick]);

  // ---- workflow:done (B1.7 / parity 153): refetch project + full re-render,
  // reselect the first row. Seam: the Workflow popup sub-phase dispatches a
  // `workflow:done` CustomEvent on window (≈ Backbone dispatcher event).
  useEffect(() => {
    const onDone = () => {
      dispatch(
        projectBoardApi.util.invalidateTags([{ type: 'Project', id: projectId }])
      );
      dispatch(
        projectBoardApi.endpoints.getAhuUnits.initiate(projectId, {
          forceRefetch: true,
        })
      )
        .unwrap()
        .then(() => {
          dispatch(selectAhu(null));
          if (groupsRef.current.length) rowClick(groupsRef.current[0].main.unit.id);
        })
        .catch(() => undefined);
    };
    window.addEventListener('workflow:done', onDone);
    return () => window.removeEventListener('workflow:done', onDone);
  }, [dispatch, projectId, rowClick]);

  // ---- Drag-reorder: mains (.ahu-main-group blocks, B1.3 / parity 151) ----
  const dragDisabled = !canMutate || !editable;
  const mainIds = useMemo(() => groups.map((g) => g.main.unit.id), [groups]);
  const draggingMainsRef = useRef(false);
  const {
    orderedIds: orderedMainIds,
    dragProps: mainDragProps,
    draggingId: draggingMainId,
  } = useAhuDragOrder({
    ids: mainIds,
    disabled: dragDisabled,
    // A press anywhere inside a versions container must not start a MAIN drag
    // (legacy group-level mousedown suppressor, ahus.js:642); the version rows
    // themselves also stopPropagation their own dragstart.
    suppressSelector: '.ahu-versions-container',
    onCommit: (ordered) =>
      reorderMains({ project_id: projectId, ordered_ids: ordered }),
    onDragStartExtra: () => {
      draggingMainsRef.current = true;
    },
    onDragEndExtra: () => {
      draggingMainsRef.current = false;
    },
  });
  const orderedGroups = useMemo(() => {
    const byId = new Map(groups.map((g) => [g.main.unit.id, g]));
    return orderedMainIds
      .map((id) => byId.get(id))
      .filter(Boolean) as AhuMainGroup[];
  }, [groups, orderedMainIds]);

  // ---- Actions ----
  const openAhu = (id: number) => {
    // redirectToAHU: same-tab navigation; the AHU board itself is Phase 6.
    window.open(`/ahu/detail/${id}`, '_self');
  };

  const findUnit = (id: number) => (units ?? []).find((u) => u.id === id);

  const permCheckClass = (action: Exclude<AhuRowAction, 'edit'>): string => {
    if (!canMutate) return ' non-clickable';
    if (
      !editable &&
      action !== 'duplicate' &&
      action !== 'create-version' &&
      action !== 'make-main'
    )
      return ' non-clickable';
    return '';
  };

  const onAction = (action: AhuRowAction, id: number) => {
    if (action === 'edit') {
      openAhu(id);
      return;
    }
    // Every mutating row action checks both permission flags (B1.2).
    if (!canMutate) return;
    switch (action) {
      case 'rename':
        dispatch(setRenamingAhu(id));
        break;
      case 'duplicate':
        dispatch(openDuplicateAhu(id));
        break;
      case 'create-version':
        dispatch(openCreateVersion(id));
        break;
      case 'delete':
        dispatch(openDeleteAhu(id));
        break;
      case 'make-main': {
        dispatch(setAhuRightPreloader(true));
        makeMainVersion({ unit_id: id })
          .unwrap()
          .then(() =>
            dispatch(
              projectBoardApi.endpoints.getAhuUnits.initiate(projectId, {
                forceRefetch: true,
              })
            ).unwrap()
          )
          .catch(() => undefined)
          .finally(() => dispatch(setAhuRightPreloader(false)));
        break;
      }
      default:
        break;
    }
  };

  // Rename commit (changeAhuName): <3 trimmed chars → revert (the row input
  // resyncs from the unchanged cache row); else PATCH {tag} (parity 145).
  const onRenameCommit = (id: number, value: string) => {
    if (value.trim().length < 3) return;
    renameAhu({ id, projectId, tag: value });
  };

  // ADD → shared New-AHU popup (B1.5); Create POSTs and the socket post path
  // refreshes + selects (parity 146).
  const onAddClick = () => {
    if (!canMutate) return;
    dispatch(openNewAhu());
  };
  const onCreateAhu = (tag: string) => {
    dispatch(setAhuRightPreloader(true));
    createAhu({ project_id: projectId, tag }).then((res) => {
      if ('data' in res && res.data) track('ahu_created');
    });
  };

  const onDuplicateConfirm = (targetProjectId: number, tag: string | null) => {
    const sourceId = modals.duplicateAhu;
    if (sourceId == null) return;
    // Duplication is async (Celery). Guard against re-entry so an impatient
    // user clicking repeatedly doesn't fire N duplications and get N copies.
    // (KA-5238)
    if (duplicatingRef.current) return;
    duplicatingRef.current = true;
    duplicateTargetRef.current = targetProjectId === projectId ? 'same_project' : 'other_project';
    dispatch(setAhuRightPreloader(true));
    duplicateAhu({ unit_id: sourceId, project_id: targetProjectId, tag });
    // Watchdog: if the backend never reports completion (stuck worker / lost
    // websocket message), don't spin forever — clear the spinner and tell the
    // user instead of leaving them stuck. (KA-5238)
    clearDuplicateWatchdog();
    duplicateWatchdogRef.current = setTimeout(() => {
      finishDuplicate();
      window.alert(
        'Duplication is taking longer than expected or may have failed. Please refresh the page and check whether the copy was created before trying again.'
      );
    }, 60000);
  };

  const onCreateVersionConfirm = (tag: string, isStatic: boolean) => {
    const sourceId = modals.createVersion;
    if (sourceId == null) return;
    dispatch(setAhuRightPreloader(true));
    // Parent auto-expanded so the new version is visible (parity 132/148).
    dispatch(expandVersionParent(sourceId));
    dispatch(setDuplicatedModelId(sourceId));
    createAhuVersion({ unit_id: sourceId, static: isStatic, tag });
  };

  const onDeleteConfirm = () => {
    const id = modals.deleteAhu;
    if (id == null) return;
    // The confirm closes on the click, not on the response: the DELETE runs in
    // the background and the right preloader is the in-flight indicator from
    // here on. Closing first also makes a second Delete click impossible.
    dispatch(closeDeleteAhu());
    dispatch(setDuplicatedModelId(null));
    dispatch(setAhuRightPreloader(true));
    // busy set suppresses the socket delete echo (B1.8 / parity 150).
    dispatch(addBusyAhu(id));
    deleteAhu({ id, projectId })
      .unwrap()
      .then(() => track('ahu_deleted'))
      .catch(() => {
        // Nothing was deleted, and no delete message is coming to clear the
        // busy marker — drop it here, then say so: the popup is already gone,
        // so this notification is the only feedback the user gets.
        dispatch(removeBusyAhu(id));
        window.alert('Delete failed. Please try again.');
      })
      .finally(() => {
        dispatch(setAhuRightPreloader(false));
        // Selection fallback to the first row runs via the units effect.
      });
  };

  const selectedUnit =
    selectedAhuId != null ? findUnit(selectedAhuId) : undefined;
  const duplicateSource =
    modals.duplicateAhu != null ? findUnit(modals.duplicateAhu) : undefined;
  const versionSource =
    modals.createVersion != null ? findUnit(modals.createVersion) : undefined;
  const deleteTarget =
    modals.deleteAhu != null ? findUnit(modals.deleteAhu) : undefined;

  if (!booted) {
    // initialize(): dotted preloader inside #main_ahus until first data load.
    return <div className="col-xs-12 dotted-preloader active 222" />;
  }

  return (
    <>
      <div className="row full-height  right-column" ref={panelRef}>
        <div
          className="col-xs-4 full-height with-border-left"
          style={{ paddingTop: 28, width: 'calc(28% + 30px)' }}
        >
          <div className="row block-header">
            <div className="col-xs-12 col-md-7 col-lg-7" style={{ paddingLeft: 21 }}>
              <div className="air-handling-title">
                <i className="icon-list btn-icon-sync" />
                <span className="block-title">Air Handling Unit</span>
              </div>
            </div>
            <button
              id="add_field_to_structure"
              className={` plus btn-add btn add${addLocked ? ' non-clickable' : ''}`}
              style={{ position: 'absolute', right: 40 }}
              disabled={addLocked}
              onClick={onAddClick}
            >
              ADD
            </button>
          </div>
          <div className="left-column-proposal-scrollable">
            <div className="row column-height">
              <div className="col-xs-12" id="ahu_list" tabIndex={0} ref={listRef}>
                {orderedGroups.map((group) => {
                  const main = group.main;
                  const mainId = main.unit.id;
                  const expanded =
                    expandedVersionParents.includes(mainId) &&
                    !draggingMainsRef.current;
                  return (
                    <div
                      className="ahu-main-group"
                      key={mainId}
                      {...mainDragProps(mainId)}
                    >
                      <AhuRow
                        unit={main.unit}
                        index={main.index}
                        isVersion={false}
                        hasVersions={group.versions.length > 0}
                        expanded={expanded}
                        selected={selectedAhuId === mainId}
                        renaming={renamingAhuId === mainId}
                        permCheckClass={permCheckClass}
                        onRowClick={rowClick}
                        onOpenAhu={openAhu}
                        onToggleVersions={(id) => dispatch(toggleVersionParent(id))}
                        onAction={onAction}
                        onRenameCommit={onRenameCommit}
                        onRenameEnd={() => dispatch(setRenamingAhu(null))}
                        dragging={draggingMainId === mainId}
                      />
                      {group.versions.length > 0 && (
                        <AhuVersionsContainer
                          group={group}
                          expanded={expanded}
                          dragDisabled={dragDisabled}
                          selectedAhuId={selectedAhuId}
                          renamingAhuId={renamingAhuId}
                          permCheckClass={permCheckClass}
                          rowClick={rowClick}
                          openAhu={openAhu}
                          onAction={onAction}
                          onRenameCommit={onRenameCommit}
                          onRenameEnd={() => dispatch(setRenamingAhu(null))}
                          reorderVersions={(mainId2, ordered) =>
                            reorderVersions({
                              main_id: mainId2,
                              ordered_ids: ordered,
                              projectId,
                            })
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <hr className="left_properties" />
        <div
          className="col-xs-8 full-height  user_settings-content-user-fields"
          style={{ width: 'calc(72% - 30px)' }}
          id="ahus_info-bar"
        >
          {selectedUnit && (
            <AhuInfoBar
              key={selectedUnit.id}
              unit={selectedUnit}
              cmsProducts={cmsProducts}
              weatherRatings={weatherRatings}
              onOpenAhu={openAhu}
            />
          )}
        </div>
        {rightPreloader && (
          <div className="col-xs-12 dotted-preloader active duplicateAHU" />
        )}
      </div>
      <div id="new_ahu_popup">
        {modals.newAhu && (
          <NewAhuPopup onCreate={onCreateAhu} onClose={() => dispatch(closeNewAhu())} />
        )}
      </div>

      {modals.duplicateAhu != null && duplicateSource && project && (
        <DuplicateAhuPopup
          sourceTag={ahuDisplayTag(duplicateSource)}
          sourceProjectId={projectId}
          sourceProjectName={project.name}
          editable={editable}
          onConfirm={onDuplicateConfirm}
          onClose={() => dispatch(closeDuplicateAhu())}
        />
      )}
      {modals.createVersion != null && versionSource && (
        <CreateVersionPopup
          sourceTag={ahuDisplayTag(versionSource)}
          onConfirm={onCreateVersionConfirm}
          onClose={() => dispatch(closeCreateVersion())}
        />
      )}
      {modals.deleteAhu != null && deleteTarget && (
        <DeleteAhuPopup
          isVersion={!!deleteTarget.parent_version_id}
          name={deleteTarget.tag}
          onConfirm={onDeleteConfirm}
          onClose={() => dispatch(closeDeleteAhu())}
        />
      )}
    </>
  );
};

/**
 * `#versions_container_{mainId}.ahu-versions-container[.collapsed]` with the
 * main's version rows; owns the within-container drag reorder
 * (`POST /ahu/versions/reorder/`, B1.3).
 */
interface AhuVersionsContainerProps {
  group: AhuMainGroup;
  expanded: boolean;
  dragDisabled: boolean;
  selectedAhuId: number | null;
  renamingAhuId: number | null;
  permCheckClass: (action: Exclude<AhuRowAction, 'edit'>) => string;
  rowClick: (id: number) => void;
  openAhu: (id: number) => void;
  onAction: (action: AhuRowAction, id: number) => void;
  onRenameCommit: (id: number, value: string) => void;
  onRenameEnd: () => void;
  reorderVersions: (mainId: number, orderedIds: number[]) => void;
}

const AhuVersionsContainer = ({
  group,
  expanded,
  dragDisabled,
  selectedAhuId,
  renamingAhuId,
  permCheckClass,
  rowClick,
  openAhu,
  onAction,
  onRenameCommit,
  onRenameEnd,
  reorderVersions,
}: AhuVersionsContainerProps) => {
  const mainId = group.main.unit.id;
  const versionIds = useMemo(
    () => group.versions.map((v) => v.unit.id),
    [group.versions]
  );
  const { orderedIds, dragProps, draggingId } = useAhuDragOrder({
    ids: versionIds,
    disabled: dragDisabled,
    onCommit: (ordered) => reorderVersions(mainId, ordered),
  });
  const byId = useMemo(
    () => new Map(group.versions.map((v) => [v.unit.id, v])),
    [group.versions]
  );

  return (
    <div
      id={`versions_container_${mainId}`}
      className={`ahu-versions-container${expanded ? '' : ' collapsed'}`}
      data-main-id={mainId}
    >
      {orderedIds.map((id) => {
        const row = byId.get(id);
        if (!row) return null;
        return (
          <AhuRow
            key={id}
            unit={row.unit}
            index={row.index}
            isVersion
            hasVersions={false}
            expanded={false}
            selected={selectedAhuId === id}
            renaming={renamingAhuId === id}
            permCheckClass={permCheckClass}
            onRowClick={rowClick}
            onOpenAhu={openAhu}
            onToggleVersions={() => undefined}
            onAction={onAction}
            onRenameCommit={onRenameCommit}
            onRenameEnd={onRenameEnd}
            dragProps={dragProps(id)}
            dragging={draggingId === id}
          />
        );
      })}
    </div>
  );
};

export default AhuListPanel;
